/* src/components/admin/announcements/edit/EditAnnouncementPage.js */
import React, { useEffect, useMemo, useState } from "react";
import { Editor } from "@hugerte/hugerte-react";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";
import {
  updateAnnouncement,
  subscribeActiveTutorCount,
  subscribeCategories,
} from "../AdminAnnouncementsLogic";
import "./EditAnnouncementPage.css";
import { backdropDismiss } from "../../../../utils/backdropDismiss";

// --- HugeRTE Imports ---
import "hugerte";
import "hugerte/themes/silver";
import "hugerte/icons/default";
import "hugerte/models/dom";
import "hugerte/skins/ui/oxide/skin.css";
import "hugerte/plugins/advlist";
import "hugerte/plugins/autolink";
import "hugerte/plugins/lists";
import "hugerte/plugins/link";
import "hugerte/plugins/image";
import "hugerte/plugins/charmap";
import "hugerte/plugins/preview";
import "hugerte/plugins/anchor";
import "hugerte/plugins/searchreplace";
import "hugerte/plugins/visualblocks";
import "hugerte/plugins/code";
import "hugerte/plugins/fullscreen";
import "hugerte/plugins/insertdatetime";
import "hugerte/plugins/media";
import "hugerte/plugins/table";
import "hugerte/plugins/wordcount";
import "hugerte/plugins/help";
import "hugerte/plugins/help/js/i18n/keynav/en.js";

export default function EditAnnouncementPage({ me, announcementId, onCancel, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);

  // Step 1 fields
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("General");
  const [savedCategories, setSavedCategories] = useState([]);
  const [contentHtml, setContentHtml] = useState("");

  // Step 2 fields
  const [audienceType, setAudienceType] = useState("all");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [notifyTutors, setNotifyTutors] = useState(false); // Always off by default on edit

  // Modal & status
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Tutor data
  const [tutors, setTutors] = useState([]);
  const [qText, setQText] = useState("");
  const [tutorCount, setTutorCount] = useState(0);

  const editorInit = useMemo(() => ({
    height: 480,
    menubar: "file edit view insert format tools table help",
    skin: false,
    content_css: false,
    ui_mode: "split",
    content_style: `body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; color: #374151; margin: 1rem; } img { max-width: 100%; height: auto; }`,
    plugins: [
      "advlist", "autolink", "lists", "link", "image", "charmap", "preview", "anchor",
      "searchreplace", "visualblocks", "code", "fullscreen", "insertdatetime", "media",
      "table", "help", "wordcount",
    ],
    toolbar: "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media table | removeformat | code fullscreen | help",
    font_family_formats: "Arial=arial,helvetica,sans-serif; Helvetica=helvetica,arial,sans-serif; Times New Roman=times new roman,times,serif; Georgia=georgia,serif; Courier New=courier new,courier,monospace; Verdana=verdana,geneva,sans-serif;",
    fontsize_formats: "10px 12px 14px 16px 18px 20px 24px 28px 32px 36px 48px",
    branding: false,
  }), []);

  useEffect(() => {
    const unsub = subscribeActiveTutorCount((n) => setTutorCount(n));
    return () => unsub?.();
  }, []);

  useEffect(() => {
    const unsub = subscribeCategories((rows) => setSavedCategories(rows || []));
    return () => unsub?.();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "users"), where("role", "==", "tutor"));
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => (u.status || "active").toLowerCase() === "active")
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTutors(rows);
    });
    return () => unsub?.();
  }, []);

  // Load existing announcement data
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, "announcements", announcementId));
        if (!alive) return;
        if (!snap.exists()) throw new Error("Announcement not found.");
        const a = snap.data() || {};
        setTitle(a.title || "");
        setCategory((a.category || "General").trim() || "General");
        setContentHtml(a.contentHtml || "");
        setAudienceType(a.audienceType || "all");
        setSelectedUserIds(a.audienceUserIds || []);
        setCommentsEnabled(a.commentsEnabled !== false);
        setNotifyTutors(false); // Always reset — admin must intentionally enable resend
      } catch (e) {
        setError(e?.message || "Failed to load.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [announcementId]);

  const filteredTutors = useMemo(() => {
    const q = (qText || "").trim().toLowerCase();
    if (!q) return tutors;
    return tutors.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(q) ||
        (t.email || "").toLowerCase().includes(q)
    );
  }, [tutors, qText]);

  const selectedSet = useMemo(() => new Set(selectedUserIds || []), [selectedUserIds]);

  const toggleTutor = (id) => {
    setSelectedUserIds((prev) => {
      const s = new Set(prev || []);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return Array.from(s);
    });
  };

  const dropdownOptions = useMemo(() => {
    const base = ["General", "Updates"];
    const saved = savedCategories.map((c) => c.name).filter((n) => !base.includes(n));
    return [...base, ...saved];
  }, [savedCategories]);

  const goToStep2 = () => {
    if (!title.trim()) {
      setError("Please enter a title before continuing.");
      return;
    }
    if (!contentHtml || !contentHtml.replace(/<[^>]*>/g, "").trim()) {
      setError("Please add some content before continuing.");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleSave = async () => {
    if (audienceType === "users" && selectedUserIds.length === 0) {
      setError("Please select at least one tutor.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await updateAnnouncement(announcementId, {
        title: title.trim(),
        category,
        contentHtml,
        audienceType,
        audienceUserIds: audienceType === "users" ? selectedUserIds : [],
        commentsEnabled: !!commentsEnabled,
        notifyTutors: !!notifyTutors, // When true, AdminAnnouncementsLogic sets notificationTriggeredAt → triggers Cloud Function
      });
      setShowConfirm(false);
      onSaved?.();
    } catch (e) {
      setError(e?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="aaPage">
        <div className="aaLoading">Loading…</div>
      </div>
    );
  }

  return (
    <div className="aaPage">

      {/* ── Page header ── */}
      <div className="aaPageHead">
        <button
          className="aaBackBtn"
          onClick={step === 1 ? onCancel : () => { setError(""); setStep(1); }}
          type="button"
        >
          {step === 1 ? "← Cancel" : "← Back"}
        </button>

        <div className="aaPageHeadCenter">
          <div className="aaPageTitle">Edit Announcement</div>
          <div className="aaStepIndicator">
            <div className={`aaStepDot ${step >= 1 ? "active" : ""}`} />
            <div className={`aaStepLine ${step >= 2 ? "active" : ""}`} />
            <div className={`aaStepDot ${step >= 2 ? "active" : ""}`} />
          </div>
          <div className="aaStepLabel">Step {step} of 2 — {step === 1 ? "Edit Content" : "Audience & Settings"}</div>
        </div>

        <div className="aaPageRight">
          {step === 1 && (
            <button className="aaBtn primary" onClick={goToStep2} type="button">
              Next
            </button>
          )}
          {step === 2 && (
            <button
              className="aaBtn primary"
              onClick={() => { setError(""); setShowConfirm(true); }}
              type="button"
            >
              Save Changes
            </button>
          )}
        </div>
      </div>

      {/* ── Step 1: Content ── */}
      {step === 1 && (
        <div className="aaFormCard">
          <div className="aaStep1Row">
            <div className="aaField" style={{ flex: 1 }}>
              <div className="aaLabel">Title</div>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Announcement title…"
                autoFocus
              />
            </div>
            <div className="aaField aaFieldCat">
              <div className="aaLabel">Category</div>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {dropdownOptions.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="aaField">
            <div className="aaLabel">Content</div>
            <div className="aaEditorWrap">
              <Editor
                value={contentHtml}
                onEditorChange={(val) => setContentHtml(val)}
                init={editorInit}
              />
            </div>
          </div>

          {error && <div className="aaError">{error}</div>}
        </div>
      )}

      {/* ── Step 2: Audience & Settings ── */}
      {step === 2 && (
        <div className="aaStep2Grid">
          {/* Audience */}
          <div className="aaFormCard">
            <div className="aaLabel">Audience</div>

            <div className="aaAudType">
              <button
                className={"aaSeg " + (audienceType === "all" ? "active" : "")}
                onClick={() => setAudienceType("all")}
                type="button"
              >
                All Tutors ({tutorCount})
              </button>
              <button
                className={"aaSeg " + (audienceType === "users" ? "active" : "")}
                onClick={() => setAudienceType("users")}
                type="button"
              >
                Select Tutors
              </button>
            </div>

            {audienceType === "users" && (
              <>
                <div className="aaAudTools">
                  <input
                    className="aaSearch"
                    placeholder="Search tutor…"
                    value={qText}
                    onChange={(e) => setQText(e.target.value)}
                  />
                  <div className="aaSelectedCount">Selected: <b>{selectedUserIds.length}</b></div>
                </div>
                <div className="aaAudList">
                  {filteredTutors.map((u) => (
                    <label key={u.id} className="aaAudItem">
                      <input
                        type="checkbox"
                        checked={selectedSet.has(u.id)}
                        onChange={() => toggleTutor(u.id)}
                      />
                      <div className="aaAudMeta">
                        <div className="aaAudName">{u.name || "Tutor"}</div>
                        <div className="aaAudEmail">{u.email || ""}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}

            {audienceType === "all" && (
              <div className="aaSideHint">This announcement will be visible to all {tutorCount} active tutors.</div>
            )}
          </div>

          {/* Publishing options */}
          <div className="aaSideCard">
            <div className="aaLabel">Publishing Options</div>

            <div className="aaToggleRow">
              <div className="aaToggleInfo">
                <div className="aaToggleLabel">Allow Comments</div>
                <div className="aaToggleDesc">Let tutors leave comments on this announcement.</div>
              </div>
              <div
                className={"aaToggleSwitch" + (commentsEnabled ? " on" : "")}
                onClick={() => setCommentsEnabled((v) => !v)}
                role="switch"
                aria-checked={commentsEnabled}
              >
                <div className="aaToggleThumb" />
              </div>
            </div>

            <div className="aaToggleDivider" />

            <div className="aaToggleRow">
              <div className="aaToggleInfo">
                <div className="aaToggleLabel">Resend Push Notification</div>
                <div className="aaToggleDesc">Off by default. Enable only if you want to re-notify tutors about this update.</div>
              </div>
              <div
                className={"aaToggleSwitch" + (notifyTutors ? " on" : "")}
                onClick={() => setNotifyTutors((v) => !v)}
                role="switch"
                aria-checked={notifyTutors}
              >
                <div className="aaToggleThumb" />
              </div>
            </div>

            {notifyTutors && (
              <div className="aaNotifyWarning">
                A push notification will be sent when you save these changes.
              </div>
            )}
          </div>
        </div>
      )}

      {error && step === 2 && (
        <div className="aaError" style={{ marginTop: 0 }}>{error}</div>
      )}

      {/* ── Confirmation modal ── */}
      {showConfirm && (
        <div className="aaModalOverlay" {...backdropDismiss(() => { if (!saving) setShowConfirm(false); })}>
          <div className="aaModal" onClick={(e) => e.stopPropagation()}>
            <div className="aaModalHead">
              <div className="aaModalTitle">Save Changes?</div>
              <p className="aaModalSub">
                Your edits to{" "}
                <strong>"{title.trim() || "Untitled"}"</strong> will be saved immediately.
                {notifyTutors && (
                  <span style={{ display: "block", marginTop: 6, color: "#dc2626", fontWeight: 600 }}>
                    A push notification will also be re-sent to tutors.
                  </span>
                )}
              </p>
            </div>

            {error && <div className="aaError" style={{ margin: "0 24px" }}>{error}</div>}

            <div className="aaModalFoot">
              <button
                className="aaBtn ghost"
                onClick={() => { setShowConfirm(false); setError(""); }}
                disabled={saving}
                type="button"
              >
                Cancel
              </button>
              <button className="aaBtn primary" onClick={handleSave} disabled={saving} type="button">
                {saving ? "Saving…" : "Yes, Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
